import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Pagination,
    Stack, TextField, Typography,
} from '@mui/material';
import ResponsiveTable from '../common/ResponsiveTable';
import { Column } from '../common/responsiveTable.types';
import SearchableSelect from '../common/SearchableSelect';
import { AuthService } from '../../services/auth.service';
import { EmployeeLoanService } from '../../services/employeeLoan.service';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { IEmployeeLoan, LoanType } from '../../interfaces/employeeLoan.interface';
import { formatMoney } from '../../utils/format';
import { formatDate } from '../../utils/dateUtils';
import { InstallmentStatusChip, LoanStatusChip, loanPeriod, loanRef, loanTypeLabel } from './loanLabels';

const PAGE_SIZE = 12;
const TYPES: { id: LoanType; label: string }[] = [
    { id: 'SALARY_ADVANCE', label: 'Salary advance (recovered from next payroll)' },
    { id: 'LOAN', label: 'Loan (recovered in installments)' },
];

// W3-D: employee self-service "My Loans". The API scopes every call to the caller's own loans.
const MyLoans: React.FC = () => {
    const { selectedInstitutionId } = useAuth();
    const toast = useToast();
    const institutionId = selectedInstitutionId || AuthService.getSelectedInstitution() || '';
    const [rows, setRows] = useState<IEmployeeLoan[]>([]);
    const [page, setPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(true);
    const [detail, setDetail] = useState<IEmployeeLoan | null>(null);
    const [requestOpen, setRequestOpen] = useState(false);
    const [loanType, setLoanType] = useState<LoanType>('SALARY_ADVANCE');
    const [amount, setAmount] = useState('');
    const [count, setCount] = useState('6');
    const [reason, setReason] = useState('');
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        if (!institutionId) {
            setLoading(false);
            return;
        }
        setLoading(true);
        try {
            const res = await EmployeeLoanService.listMine(institutionId, page, PAGE_SIZE);
            setRows(res.content);
            setTotalPages(res.totalPages);
        } catch (e) {
            toast.showApiError(e, 'Failed to load your loans');
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [institutionId, page]);

    useEffect(() => { load(); }, [load]);

    const openDetail = async (id: string) => {
        try {
            setDetail(await EmployeeLoanService.getMine(id));
        } catch (e) {
            toast.showApiError(e, 'Failed to load the loan');
        }
    };

    const submitRequest = async () => {
        setSaving(true);
        try {
            await EmployeeLoanService.requestMine({
                institutionId,
                loanType,
                principal: Number(amount),
                installmentCount: loanType === 'LOAN' ? Number(count) : null,
                reason: reason.trim() || null,
            });
            setRequestOpen(false);
            setAmount('');
            setReason('');
            toast.showSuccess('Request sent for approval');
            await load();
        } catch (e) {
            toast.showApiError(e, 'Failed to send the request');
        } finally {
            setSaving(false);
        }
    };

    const cancel = async (id: string) => {
        try {
            await EmployeeLoanService.cancelMine(id);
            toast.showSuccess('Request cancelled');
            setDetail(null);
            await load();
        } catch (e) {
            toast.showApiError(e, 'Failed to cancel the request');
        }
    };

    const columns: Column<IEmployeeLoan>[] = [
        { key: 'ref', header: 'Loan no.', priority: 1, helpKey: 'payroll.myLoans.col.ref', render: r => loanRef(r.id) },
        { key: 'type', header: 'Type', priority: 1, helpKey: 'payroll.myLoans.col.type', render: r => loanTypeLabel(r.loanType) },
        { key: 'principal', header: 'Amount', priority: 1, align: 'right', helpKey: 'payroll.myLoans.col.principal', render: r => formatMoney(r.principal) },
        { key: 'out', header: 'Still to repay', priority: 1, align: 'right', helpKey: 'payroll.myLoans.col.out', render: r => formatMoney(r.outstanding) },
        { key: 'status', header: 'Status', priority: 2, helpKey: 'payroll.myLoans.col.status', render: r => <LoanStatusChip status={r.status} /> },
        { key: 'actions', header: 'Actions', role: 'actions', hideInCard: true, render: r => <Button size="small" data-help="payroll.myLoans.action.view" onClick={() => openDetail(r.id)}>View</Button> },
    ];

    const valid = Number(amount) > 0 && (loanType === 'SALARY_ADVANCE' || (Number(count) >= 1 && Number(count) <= 120));

    return (
        <Box sx={{ p: { xs: 1.5, sm: 3 } }} data-help-page="payroll.myLoans.page">
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }} data-help-section="payroll.myLoans.toolbar">
                <Typography variant="h5">My Loans</Typography>
                <Button data-help="payroll.myLoans.request" variant="contained" onClick={() => setRequestOpen(true)} disabled={!institutionId}>Request a loan or advance</Button>
            </Box>
            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
            ) : (
                <>
                    <ResponsiveTable data-help-section="payroll.myLoans.table" pageKey="my-loans" columns={columns} rows={rows} getRowKey={r => r.id}
                        emptyMessage="You have no loans or salary advances." />
                    {totalPages > 1 && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                            <Pagination data-help="payroll.myLoans.pager" count={totalPages} page={page + 1} onChange={(_, p) => setPage(p - 1)} />
                        </Box>
                    )}
                </>
            )}

            <Dialog open={requestOpen} onClose={() => setRequestOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Request a loan or salary advance</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <SearchableSelect data-help="payroll.myLoans.form.type" label="Type" options={TYPES} value={loanType} onChange={id => id && setLoanType(id as LoanType)}
                            getOptionLabel={t => t.label} clearable={false} />
                        <TextField data-help="payroll.myLoans.form.amount" label="Amount" type="number" value={amount} onChange={e => setAmount(e.target.value)} required fullWidth
                            inputProps={{ min: 0.01, step: '0.01' }} />
                        {loanType === 'LOAN' && (
                            <TextField data-help="payroll.myLoans.form.count" label="Number of monthly installments" type="number" value={count} onChange={e => setCount(e.target.value)}
                                inputProps={{ min: 1, max: 120 }} fullWidth />
                        )}
                        <TextField data-help="payroll.myLoans.form.reason" label="Reason" value={reason} onChange={e => setReason(e.target.value)} multiline minRows={2} fullWidth
                            inputProps={{ maxLength: 500 }} />
                        <Alert severity="info">Your manager approves the request; the money is then paid out and recovered from your payroll.</Alert>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRequestOpen(false)} disabled={saving}>Cancel</Button>
                    <Button data-help="payroll.myLoans.form.send" variant="contained" onClick={submitRequest} disabled={!valid || saving}>Send request</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="sm" fullWidth>
                <DialogTitle>{detail ? `${loanTypeLabel(detail.loanType)} ${loanRef(detail.id)}` : 'Loan'}</DialogTitle>
                <DialogContent dividers data-help-section="payroll.myLoans.detail">
                    {detail && (
                        <>
                            <Stack direction="row" justifyContent="space-between" sx={{ mb: 1 }}>
                                <LoanStatusChip status={detail.status} />
                                <Typography variant="body2">Still to repay: {formatMoney(detail.outstanding)}</Typography>
                            </Stack>
                            <Typography variant="body2">Amount {formatMoney(detail.principal)}
                                {detail.installmentCount > 0 && ` in ${detail.installmentCount} installment(s) of ${formatMoney(detail.installmentAmount)}`}
                            </Typography>
                            {detail.reason && <Typography variant="body2" color="text.secondary">Reason: {detail.reason}</Typography>}
                            {detail.rejectReason && <Alert severity="error" sx={{ mt: 1 }}>Rejected: {detail.rejectReason}</Alert>}
                            {(detail.installments ?? []).length > 0 && (
                                <Box sx={{ mt: 2 }} data-help="payroll.myLoans.schedule">
                                    <Typography variant="subtitle2">Schedule</Typography>
                                    {(detail.installments ?? []).map(i => (
                                        <Stack key={i.id} direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 0.5, flexWrap: 'wrap', gap: 1 }}>
                                            <Typography variant="body2">{loanPeriod(i.dueYear, i.dueMonth)}</Typography>
                                            <Typography variant="body2">{formatMoney(i.scheduledAmount)}</Typography>
                                            <InstallmentStatusChip status={i.status} />
                                        </Stack>
                                    ))}
                                </Box>
                            )}
                            {detail.disbursedDate && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                    Paid out on {formatDate(detail.disbursedDate)}
                                </Typography>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    {detail?.status === 'REQUESTED' && <Button color="error" data-help="payroll.myLoans.action.cancel" onClick={() => cancel(detail.id)}>Cancel request</Button>}
                    <Button onClick={() => setDetail(null)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default MyLoans;
